import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../../../../API';
import { CASEBUILDER_BASE } from '../../../../../constants/setupRoadmapSteps';
import { getNaccIdFromDetails, setNaccIdInDetails } from '../../../../../helpers/naccAlias';

/**
 * Centralized case document state and mutations.
 */
export function useCaseWorkspace({ data, name }) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [doc, setDoc] = useState(data);
    const [localIdentifierDraft, setLocalIdentifierDraft] = useState(data?.localIdentifier ?? '');
    const [naccIdDraft, setNaccIdDraft] = useState(getNaccIdFromDetails(data?.details));
    const [selectedProtocolId, setSelectedProtocolId] = useState(data?.protocolId ?? null);
    const [createPending, setCreatePending] = useState(false);
    const [updatePending, setUpdatePending] = useState(false);
    const [naccUpdatePending, setNaccUpdatePending] = useState(false);
    const [naccClinicalPending, setNaccClinicalPending] = useState(false);
    const [naccClinicalStatus, setNaccClinicalStatus] = useState(null);
    const [deletePending, setDeletePending] = useState(false);
    const [quarantinePending, setQuarantinePending] = useState(false);
    const [affectedItemsAfterBlockRemoval, setAffectedItemsAfterBlockRemoval] = useState(null);

    const hasCase = !!doc?._id;
    const nameChanged = hasCase && (localIdentifierDraft !== (doc?.localIdentifier ?? ''));
    const savedNaccId = getNaccIdFromDetails(doc?.details);
    const naccChanged = hasCase && ((naccIdDraft ?? '').trim() !== (savedNaccId ?? '').trim());
    const caseIdentity = name ?? data?.networkIdentifier ?? data?._id ?? null;
    const caseId = doc?.networkIdentifier ?? name;

    useEffect(() => {
        setDoc(data);
        setLocalIdentifierDraft(data?.localIdentifier ?? '');
        setNaccIdDraft(getNaccIdFromDetails(data?.details));
        setSelectedProtocolId(data?.protocolId ?? null);
        setAffectedItemsAfterBlockRemoval(null);
        setCreatePending(false);
        setUpdatePending(false);
        setNaccUpdatePending(false);
        setNaccClinicalPending(false);
        setNaccClinicalStatus(null);
        setDeletePending(false);
        setQuarantinePending(false);
    }, [caseIdentity, data]);

    useEffect(() => {
        setLocalIdentifierDraft(doc?.localIdentifier ?? '');
    }, [doc?.localIdentifier]);

    const { data: protocolsResponse } = useQuery({
        queryKey: ['bdsa-protocol'],
        queryFn: () => api.get('/bdsa/protocol'),
        staleTime: 5 * 60 * 1000,
    });

    const availableProtocols = protocolsResponse?.status === 200 && Array.isArray(protocolsResponse.data)
        ? protocolsResponse.data
        : [];

    const selectedProtocol = useMemo(() => {
        if (!selectedProtocolId || !availableProtocols.length) return null;
        return availableProtocols.find((p) => p._id === selectedProtocolId) || null;
    }, [selectedProtocolId, availableProtocols]);

    useEffect(() => {
        if (doc?.protocolId !== undefined) {
            setSelectedProtocolId(doc.protocolId ?? null);
        }
    }, [doc?.protocolId]);

    const invalidateCaseQueries = useCallback((caseDoc) => {
        queryClient.invalidateQueries({ queryKey: ['bdsa-case-list'] });
        const networkId = caseDoc?.networkIdentifier;
        if (networkId) {
            queryClient.invalidateQueries({ queryKey: ['bdsa-case', networkId] });
        }
    }, [queryClient]);

    const importNaccClinicalForCase = useCallback(async (caseDoc, naccid) => {
        const id = caseDoc?._id;
        const trimmed = (naccid ?? '').trim();
        if (!id || !trimmed) return caseDoc;
        setNaccClinicalPending(true);
        setNaccClinicalStatus(null);
        try {
            const response = await api.post(`/bdsa/case/${id}/import-nacc-clinical`, {
                query: { naccid: trimmed },
            });
            if (response.status === 200 && response.data?.case) {
                const fields = response.data.importedFields || [];
                setNaccClinicalStatus({
                    ok: true,
                    message: fields.length
                        ? `Imported ${fields.length} clinical field${fields.length === 1 ? '' : 's'} from NACC`
                        : 'NACC clinical record found (no fields to import)',
                });
                invalidateCaseQueries(response.data.case);
                return response.data.case;
            }
            const detail = response?.data?.message
                || (typeof response?.data === 'string' ? response.data : null)
                || 'NACC clinical import failed';
            setNaccClinicalStatus({
                ok: false,
                message: response.status === 404
                    ? `No NACC clinical record found for ${trimmed}`
                    : detail,
            });
            return caseDoc;
        } catch (err) {
            setNaccClinicalStatus({
                ok: false,
                message: err?.message || 'NACC clinical import failed',
            });
            return caseDoc;
        } finally {
            setNaccClinicalPending(false);
        }
    }, [invalidateCaseQueries]);

    const protocolErrorMessage = (response, fallback) => (
        response?.data?.message
        || response?.data?.error
        || (typeof response?.data === 'string' ? response.data : null)
        || fallback
    );

    const handleProtocolSelect = useCallback((protocol) => {
        const newProtocolId = protocol?._id ?? null;
        const currentProtocolId = doc?.protocolId ?? null;

        // Same protocol: no-op here. loadProtocol re-applies the template.
        if (String(currentProtocolId || '') === String(newProtocolId || '')) {
            return Promise.resolve();
        }

        setSelectedProtocolId(newProtocolId);
        if (!doc?._id) {
            if (doc) setDoc({ ...doc, protocolId: newProtocolId });
            return Promise.resolve();
        }

        return api.put(`/bdsa/case/${doc._id}`, {
            body: {
                protocol_id: newProtocolId,
                schemaId: doc.schemaId,
                details: doc.details ?? {},
                localIdentifier: doc.localIdentifier,
                quarantined: doc.quarantined,
            },
        }).then((response) => {
            if (response.status !== 200 || !response.data) {
                throw new Error(protocolErrorMessage(response, 'Failed to load protocol'));
            }
            const affectedIds = response.data.affectedItemIds;
            if (Array.isArray(affectedIds) && affectedIds.length > 0) {
                setAffectedItemsAfterBlockRemoval(affectedIds);
            }
            setDoc(response.data);
            return response.data;
        });
    }, [doc]);

    const loadProtocol = useCallback(async (protocol) => {
        const newProtocolId = protocol?._id ?? null;
        if (!newProtocolId) {
            throw new Error('No protocol selected');
        }
        const isSame = String(doc?.protocolId || '') === String(newProtocolId);
        if (!isSame) {
            return handleProtocolSelect(protocol);
        }
        if (!doc?._id) {
            return null;
        }
        const response = await api.post(`/bdsa/case/${doc._id}/refresh-protocol-blocks`);
        if (response.status !== 200) {
            throw new Error(protocolErrorMessage(response, 'Failed to load protocol'));
        }
        const affectedIds = response.data?.affectedItemIds;
        if (Array.isArray(affectedIds) && affectedIds.length > 0) {
            setAffectedItemsAfterBlockRemoval(affectedIds);
        }
        invalidateCaseQueries(doc);
        const networkId = doc.networkIdentifier || caseId;
        if (networkId) {
            const caseRes = await api.get('bdsa/case', { query: { networkIdentifier: networkId } });
            if (caseRes?.status === 200 && caseRes?.data) {
                setDoc(caseRes.data);
            }
        }
        return response.data;
    }, [doc, caseId, handleProtocolSelect, invalidateCaseQueries]);

    const handleDefineManually = useCallback(() => {
        if (!doc?._id) return;
        api.put(`/bdsa/case/${doc._id}`, {
            body: {
                protocol_id: null,
                schemaId: doc.schemaId,
                details: doc.details ?? {},
                localIdentifier: doc.localIdentifier,
                quarantined: doc.quarantined,
            },
        }).then((response) => {
            if (response.status === 200 && response.data) {
                setDoc(response.data);
                setSelectedProtocolId(null);
            }
        }).catch((err) => console.error('Failed to clear protocol:', err));
    }, [doc]);

    const handleCreateCase = useCallback((options = {}) => {
        const localIdentifier = (options.localIdentifier ?? localIdentifierDraft ?? '').trim();
        const nacc = options.naccId ?? naccIdDraft;
        const protocolId = options.protocolId ?? null;
        if (!localIdentifier) return Promise.resolve(null);
        setCreatePending(true);
        setNaccClinicalStatus(null);
        const details = setNaccIdInDetails({}, nacc);
        return api.post('/bdsa/case', {
            body: {
                localIdentifier,
                quarantined: true,
                details,
                ...(protocolId ? { protocol_id: protocolId } : {}),
            },
        }).then(async (response) => {
            if (response.status !== 200 || !response.data) {
                setCreatePending(false);
                return null;
            }
            let caseDoc = response.data;
            if ((nacc ?? '').trim()) {
                caseDoc = await importNaccClinicalForCase(caseDoc, nacc);
            }
            setCreatePending(false);
            queryClient.invalidateQueries({ queryKey: ['bdsa-case-list'] });
            return caseDoc;
        }).catch(() => {
            setCreatePending(false);
            return null;
        });
    }, [localIdentifierDraft, naccIdDraft, importNaccClinicalForCase, queryClient]);

    const handleUpdateCaseName = useCallback((nextLocalId) => {
        const localIdentifier = (nextLocalId ?? localIdentifierDraft ?? '').trim();
        if (!hasCase || !localIdentifier) return Promise.resolve();
        if (nextLocalId !== undefined) setLocalIdentifierDraft(nextLocalId);
        const changed = nextLocalId !== undefined
            ? localIdentifier !== (doc?.localIdentifier ?? '')
            : nameChanged;
        if (!changed) return Promise.resolve();
        setUpdatePending(true);
        return api.put(`/bdsa/case/${doc._id}`, {
            body: {
                localIdentifier,
                schemaId: doc.schemaId,
                details: doc.details ?? {},
                quarantined: doc.quarantined,
                protocol_id: doc.protocolId ?? null,
            },
        }).then((response) => {
            setUpdatePending(false);
            if (response.status === 200 && response.data) {
                setDoc(response.data);
                invalidateCaseQueries(response.data);
            }
        }).catch(() => setUpdatePending(false));
    }, [hasCase, localIdentifierDraft, nameChanged, doc, invalidateCaseQueries]);

    const handleUpdateNaccId = useCallback(() => {
        if (!hasCase || !naccChanged) return Promise.resolve();
        setNaccUpdatePending(true);
        setNaccClinicalStatus(null);
        const details = setNaccIdInDetails(doc.details, naccIdDraft);
        return api.put(`/bdsa/case/${doc._id}`, {
            body: {
                localIdentifier: doc.localIdentifier,
                schemaId: doc.schemaId,
                details,
                quarantined: doc.quarantined,
                protocol_id: doc.protocolId ?? null,
            },
        }).then(async (response) => {
            setNaccUpdatePending(false);
            if (response.status !== 200 || !response.data) return;
            let caseDoc = response.data;
            setDoc(caseDoc);
            invalidateCaseQueries(caseDoc);
            if ((naccIdDraft ?? '').trim()) {
                caseDoc = await importNaccClinicalForCase(caseDoc, naccIdDraft);
                setDoc(caseDoc);
            }
        }).catch(() => setNaccUpdatePending(false));
    }, [hasCase, naccChanged, doc, naccIdDraft, invalidateCaseQueries, importNaccClinicalForCase]);

    const handlePullNaccClinical = useCallback(async () => {
        if (!hasCase) return;
        const naccid = (naccIdDraft ?? '').trim() || savedNaccId;
        if (!naccid) {
            setNaccClinicalStatus({ ok: false, message: 'Enter a NACC ID first' });
            return;
        }
        if (naccChanged) {
            handleUpdateNaccId();
            return;
        }
        const caseDoc = await importNaccClinicalForCase(doc, naccid);
        setDoc(caseDoc);
    }, [hasCase, naccIdDraft, savedNaccId, naccChanged, handleUpdateNaccId, importNaccClinicalForCase, doc]);

    const handleDeleteCase = useCallback(() => {
        if (!hasCase) return Promise.resolve(false);
        setDeletePending(true);
        return api.delete(`/bdsa/case/${doc._id}`).then((response) => {
            setDeletePending(false);
            if (response.status === 200) {
                setDoc(null);
                setLocalIdentifierDraft('');
                setNaccIdDraft('');
                queryClient.invalidateQueries({ queryKey: ['bdsa-case-list'] });
                navigate(CASEBUILDER_BASE);
                return true;
            }
            return false;
        }).catch(() => {
            setDeletePending(false);
            return false;
        });
    }, [hasCase, doc, queryClient, navigate]);

    const handleQuarantinedChange = useCallback((quarantined) => {
        if (!hasCase) return;
        setQuarantinePending(true);
        api.put(`/bdsa/case/${doc._id}`, {
            body: {
                localIdentifier: doc.localIdentifier,
                schemaId: doc.schemaId,
                details: doc.details ?? {},
                quarantined,
                protocol_id: doc.protocolId ?? null,
            },
        }).then((response) => {
            setQuarantinePending(false);
            if (response.status === 200 && response.data) {
                setDoc(response.data);
            }
        }).catch(() => setQuarantinePending(false));
    }, [hasCase, doc]);

    const onSave = useCallback((savedDoc) => {
        setDoc(savedDoc);
        if (savedDoc?.protocolId !== undefined) {
            setSelectedProtocolId(savedDoc.protocolId ?? null);
        }
    }, []);

    return {
        doc,
        setDoc,
        caseId,
        hasCase,
        localIdentifierDraft,
        setLocalIdentifierDraft,
        naccIdDraft,
        setNaccIdDraft,
        savedNaccId,
        nameChanged,
        naccChanged,
        selectedProtocolId,
        setSelectedProtocolId,
        selectedProtocol,
        availableProtocols,
        createPending,
        updatePending,
        naccUpdatePending,
        naccClinicalPending,
        naccClinicalStatus,
        deletePending,
        quarantinePending,
        affectedItemsAfterBlockRemoval,
        setAffectedItemsAfterBlockRemoval,
        handleProtocolSelect,
        loadProtocol,
        handleDefineManually,
        handleCreateCase,
        handleUpdateCaseName,
        handleUpdateNaccId,
        handlePullNaccClinical,
        handleDeleteCase,
        handleQuarantinedChange,
        onSave,
        importNaccClinicalForCase,
    };
}
